import { useMemo } from 'react';
import Tooltip, { TooltipProps } from '@mui/material/Tooltip';
import styled from 'styled-components';
import { formatJson } from '../../../utils/jsonFormatting';

interface Props {
  payload: string;
}

/** The tooltip is a peek at the message - anything longer is read in the dialog. */
const MAX_PREVIEW_LINES = 40;

const PAYLOAD_MARKER = '{…}';

const toPreview = (formatted: string): string => {
  const lines = formatted.split('\n');

  if (lines.length <= MAX_PREVIEW_LINES) return formatted;

  return `${lines.slice(0, MAX_PREVIEW_LINES).join('\n')}\n…`;
};

/**
 * The payload column. The column is too narrow for the json to say anything, so the cell
 * is just a handle - hovering it shows the message pretty printed, and the dialog is only
 * needed for the long ones.
 */
const PayloadCell = (props: Props) => {
  const { payload } = props;

  const preview = useMemo(() => toPreview(formatJson(payload)), [payload]);

  if (!payload) return <>-</>;

  return (
    <PayloadTooltip placement="right-start" enterDelay={300} title={<PreviewText>{preview}</PreviewText>}>
      <PayloadText>{PAYLOAD_MARKER}</PayloadText>
    </PayloadTooltip>
  );
};

const PayloadTooltip = styled(({ className, ...rest }: TooltipProps) => (
  <Tooltip {...rest} classes={{ popper: className }} />
))`
  & .MuiTooltip-tooltip {
    max-width: 640px;
    padding: ${(props) => props.theme.spacing(3)} ${(props) => props.theme.spacing(4)};
    background: ${(props) => props.theme.palette.grey.black90};
  }
`;

const PreviewText = styled.pre`
  margin: 0;
  max-height: 60vh;
  overflow: auto;

  font-family: 'Courier New', Courier, monospace;
  font-size: 12px;
  line-height: 1.5;
  tab-size: 2;
  white-space: pre;
`;

const PayloadText = styled.span`
  display: block;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;

  font-family: 'Courier New', Courier, monospace;
  font-size: 12px;
  color: ${(props) => props.theme.palette.functions.action.primary};
  text-decoration: underline;
  cursor: pointer;
`;

export default PayloadCell;
